import { AuthService } from './authService';
import {
  AdminPagedResponse,
  AdminInspectionSummaryDto,
  AdminInspectionTableSchemaDto,
} from '../types/adminDataInspection';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

interface PagedQueryParams {
  pageNumber: number;
  pageSize: number;
  sortColumn?: string;
  sortDirection?: string;
  filters?: Record<string, string>;
}

class AdminDataInspectionService {
  private schemaCache = new Map<string, Promise<AdminInspectionTableSchemaDto>>();

  /**
   * Get summary row counts for all tables
   */
  async getSummary(): Promise<AdminInspectionSummaryDto> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/admin-data-inspection/summary`
    );

    if (!response.ok) {
      throw new Error(`Failed to get summary: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Column schema for a table (all columns, types, key columns, status lookups).
   * Cached per table for the lifetime of the page.
   */
  getTableSchema(tableKey: string): Promise<AdminInspectionTableSchemaDto> {
    const cached = this.schemaCache.get(tableKey);
    if (cached) return cached;

    const promise = (async () => {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/admin-data-inspection/tables/${tableKey}/schema`
      );
      if (!response.ok) {
        throw new Error(`Failed to get schema for ${tableKey}: ${response.status} ${response.statusText}`);
      }
      return (await response.json()) as AdminInspectionTableSchemaDto;
    })();

    // Don't cache failures
    promise.catch(() => this.schemaCache.delete(tableKey));
    this.schemaCache.set(tableKey, promise);
    return promise;
  }

  /**
   * Generic paged table fetch — works for any registered table key
   */
  async getPagedTable<T>(tableKey: string, params: PagedQueryParams): Promise<AdminPagedResponse<T>> {
    const searchParams = new URLSearchParams();
    searchParams.set('pageNumber', params.pageNumber.toString());
    searchParams.set('pageSize', params.pageSize.toString());

    if (params.sortColumn) {
      searchParams.set('sortColumn', params.sortColumn);
    }
    if (params.sortDirection) {
      searchParams.set('sortDirection', params.sortDirection);
    }

    // Add filters with "filter." prefix
    if (params.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) {
          searchParams.set(`filter.${key}`, value);
        }
      }
    }

    const url = `${API_BASE_URL}/admin-data-inspection/tables/${tableKey}?${searchParams.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get ${tableKey}: ${response.status} ${response.statusText}. ${errorText}`);
    }

    return await response.json();
  }
}

export const adminDataInspectionService = new AdminDataInspectionService();
